import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/button'
import { SearchInput } from '../../../components/inputs/searchInput'
import { SelectProductModal } from '../../../components/modal/app_modals/selectProductModal'
import { ProductTileContainer } from '../../../components/product/productTile'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useAddCategoryToProductsMutation,
  useGetAllProductsQuery,
  useRemoveProductsFromSubCategoryMutation,
} from '../../../redux-toolkits/products/products.slice'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { Product } from '../../../redux-toolkits/products/products.type'
export const SubCategoryProducts = ({
  subcategoryProducts,
  subCategoryId,
  categoryId,
  setSubcategoryProducts,
  refetchSubCategory = () => {},
}: {
  subcategoryProducts: Product[]
  subCategoryId: string
  categoryId: string
  setSubcategoryProducts: (picks: Product[]) => void
  refetchSubCategory?: () => void
}) => {
  const [subCatQueryParam, setSubCatQueryParam] = useState<QueryProps>({
    search: undefined,
    page: 1,
    limit: 500,
  })
  const [itemsToAdd, setItemsToAdd] = useState<string[]>([])
  const [itemsToRemove, setItemsToRemove] = useState<string[]>([])

  const navigate = useNavigate()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const [addCategoryToProducts, { isLoading: addingCategoryToProducts }] =
    useAddCategoryToProductsMutation()

  const [removeProductsFromSubCategory, { isLoading: removingProducts }] =
    useRemoveProductsFromSubCategoryMutation()

  const {
    data: subCategoryProductsResponse,
    isFetching: fetchingSubCategoryProducts,
  } = useGetAllProductsQuery(
    {
      ...subCatQueryParam,
      skip: (Number(subCatQueryParam.page) - 1) * 6,
      subcategoryId: subCategoryId,
      page: undefined,
    },
    { refetchOnMountOrArgChange: true },
  )

  useEffect(() => {
    setSubcategoryProducts(subCategoryProductsResponse?.data?.data ?? [])
  }, [subCategoryProductsResponse])

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <SearchInput
          searchFunction={(val) => {
            setSubCatQueryParam({
              ...subCatQueryParam,
              page: 1,
              search: val || undefined,
            })
          }}
          placeholder="Search subcategory products"
          className="!w-full"
        />

        <Button
          type="button"
          onClick={async () => {
            if (
              JSON.stringify(subcategoryProducts) !==
              JSON.stringify(subCategoryProductsResponse?.data?.data ?? [])
            ) {
              if (itemsToAdd.length > 0) {
                const resp = await addCategoryToProducts({
                  categoryId: categoryId,
                  subcategoryId: subCategoryId,
                  productIds: itemsToAdd,
                })

                if (resp.data?.success) {
                  refetchSubCategory()
                }
              }

              if (itemsToRemove.length > 0) {
                const resp = await removeProductsFromSubCategory({
                  id: subCategoryId,
                  products: itemsToRemove,
                })
                if (resp.data?.success) {
                  refetchSubCategory()
                }
              }
            } else {
              handleModalOpen('addProductsToSubCategory')
            }
          }}
          primary
          className={classNames('max-w-fit', {
            hidden: fetchingSubCategoryProducts,
          })}
          label={
            JSON.stringify(subcategoryProducts) !==
            JSON.stringify(subCategoryProductsResponse?.data?.data ?? [])
              ? 'Save'
              : 'Manage Products'
          }
          size="medium"
          loading={addingCategoryToProducts || removingProducts}
          disabled={addingCategoryToProducts || removingProducts}
        />
      </div>

      <ProductTileContainer
        products={subcategoryProducts}
        handleSortEnd={(products) => {
          setSubcategoryProducts(products)
        }}
        canDelete={false}
        onDeleteProduct={(p, idx) => {}}
        sortable={false}
        onViewProduct={(p) => {
          navigate(`/products/view/${p?._id}`)
        }}
        loading={fetchingSubCategoryProducts}
      />

      <SelectProductModal
        open={modal === 'addProductsToSubCategory' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        selectedProducts={subcategoryProducts}
        onSuccessCallback={(products) => {
          setSubcategoryProducts([
            ...subcategoryProducts.filter((p) =>
              products.map((i) => i._id).includes(p._id),
            ),
            ...products.filter(
              (p) => !subcategoryProducts.map((i) => i._id).includes(p._id),
            ),
          ])

          // Filter out unselected products for removal
          setItemsToRemove([
            ...subcategoryProducts
              .filter((p) => !products.map((i) => i._id).includes(p._id))
              .map((i) => i._id),
          ])

          // Filter out newly selected products for addition
          setItemsToAdd([
            ...products
              .filter(
                (p) => !subcategoryProducts.map((i) => i._id).includes(p._id),
              )
              .map((i) => i._id),
          ])

          handleModalClose()
        }}
        query={undefined}
      />
    </>
  )
}
